import React, { useMemo } from 'react';
import {
  Label,
  HStack,
  Text,
  Card,
  Table,
  Button,
  Tooltip,
} from '@adc/parallax-component-library';
import { ParticipantSymptomNotesConfiguration } from 'adc-sirius-module-common';
import EditParticipantNoteModal from '../../modal/EditParticipantNoteModal';
import {
  studyQueryOptions,
  updateStudyMutateOptions,
} from '../../../queryOptions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Study } from '../../../models';
import './ConfigTable.scss';
import i18n from '../../../utilities/i18n';

type Props = {
  study: Study;
  canEdit: boolean;
};

const formatDuration = (rawValueMinutes) => {
  const rawValue = rawValueMinutes ?? 0;
  const days = Math.floor(rawValue / 1440);
  const hours = Math.floor((rawValue % 1440) / 60);
  const minutes = rawValue % 60;

  let formattedValue = '';

  if (days > 0) {
    formattedValue += `${days}${i18n.t('Common.unitDays_short')}`;
  }

  if (hours > 0) {
    formattedValue += `${hours}${i18n.t('Common.unitHours_short')}`;
  }

  if (minutes > 0) {
    formattedValue += `${minutes}${i18n.t('Common.unitMinutes_short')}`;
  }

  return formattedValue;
};

const formatTime = (rawValue) => {
  if (!rawValue) {
    return '';
  }

  const date = new Date(`1970-01-01T${rawValue}`);

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * ConfigTable component
 * @param {Study} study The study object
 * @param {boolean} canEdit Whether the user can edit the configuration
 * @returns {React.JSX.Element}
 */
export const ConfigTable = ({ study, canEdit }: Props) => {
  const updateStudy = useMutation(updateStudyMutateOptions());
  const queryClient = useQueryClient();
  const participantSymptomNotes = study.configuration?.participantSymptomNotes;
  const choices = participantSymptomNotes?.choices ?? [];
  const fullChoicesText =
    choices.length > 0
      ? choices.join(', ')
      : i18n.t('Page_Study.table_config.notConfigured');

  const onSave = (
    values: ParticipantSymptomNotesConfiguration,
    updateReason: string,
  ) =>
    updateStudy
      .mutateAsync({
        studyKey: study.key,
        updatedStudyValues: {
          ...study,
          updateReason,
          configuration: {
            ...study.configuration,
            participantSymptomNotes: { ...values },
          },
        },
      })
      .then(() => {
        const studyQueryKey = studyQueryOptions({
          key: study.key,
        }).queryKey;

        queryClient.invalidateQueries({ queryKey: studyQueryKey });
      });

  const enabledLabel = useMemo(
    () => (
      <Label
        aria-label={i18n.t('Modal_ParticipantNote.enabled')}
        variant="tertiary"
        testID="enabled"
      >
        <Label.Text type="$bodySmall.medium">
          {i18n.t('Modal_ParticipantNote.enabled')}
        </Label.Text>
        <Label.Text> </Label.Text>
        <Label.Text>
          {i18n.t(`Common.${participantSymptomNotes?.enabled ? 'yes' : 'no'}`)}
        </Label.Text>
      </Label>
    ),
    [participantSymptomNotes?.enabled],
  );

  const intervalPromptsLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.intervalPrompts')}
          variant="tertiary"
          testID="interval-prompt"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.intervalPrompts')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {i18n.t(
              `Common.${participantSymptomNotes.intervalPrompts ? 'yes' : 'no'}`,
            )}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.intervalPrompts,
  ]);

  const intervalFrequencyLabel = useMemo(() => {
    if (
      participantSymptomNotes?.enabled &&
      participantSymptomNotes?.intervalPrompts
    ) {
      const formattedValue = formatDuration(
        participantSymptomNotes?.intervalFrequencyMinutes,
      );

      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.intervalFrequency')}
          variant="tertiary"
          testID="interval-frequency"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.intervalFrequency')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>{`${formattedValue || i18n.t('Page_Study.table_config.notConfigured')}`}</Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.intervalFrequencyMinutes,
    participantSymptomNotes?.intervalPrompts,
  ]);

  const reminderFrequencyLabel = useMemo(() => {
    if (
      participantSymptomNotes?.enabled &&
      participantSymptomNotes?.intervalPrompts
    ) {
      const formattedValue = formatDuration(
        participantSymptomNotes?.reminderFrequencyMinutes,
      );

      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.reminderFrequency')}
          variant="tertiary"
          testID="reminder-frequency"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.reminderFrequency')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>{`${formattedValue || i18n.t('Page_Study.table_config.notConfigured')}`}</Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.intervalPrompts,
    participantSymptomNotes?.reminderFrequencyMinutes,
  ]);

  const firstPromptTimeLabel = useMemo(() => {
    if (
      participantSymptomNotes?.enabled &&
      participantSymptomNotes?.intervalPrompts
    ) {
      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.firstPromptTime')}
          variant="tertiary"
          testID="first-prompt-time"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.firstPromptTime')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {formatTime(participantSymptomNotes?.firstPromptTime) ||
              i18n.t('Page_Study.table_config.notConfigured')}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.firstPromptTime,
    participantSymptomNotes?.intervalPrompts,
  ]);

  const promptWindowLabel = useMemo(() => {
    if (
      participantSymptomNotes?.enabled &&
      participantSymptomNotes?.intervalPrompts
    ) {
      const startTime = formatTime(participantSymptomNotes?.promptStartTime);
      const endTime = formatTime(participantSymptomNotes?.promptEndTime);

      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.promptWindow')}
          variant="tertiary"
          testID="prompt-window"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.promptWindow')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {startTime && endTime
              ? `${startTime} - ${endTime}`
              : i18n.t('Page_Study.table_config.notConfigured')}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.intervalPrompts,
    participantSymptomNotes?.promptEndTime,
    participantSymptomNotes?.promptStartTime,
  ]);

  const choicesLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Tooltip text={fullChoicesText}>
          <Label
            aria-label={i18n.t('Modal_ParticipantNote.choices')}
            variant="tertiary"
            testID="choices"
          >
            <Label.Text type="$bodySmall.medium">
              {i18n.t('Modal_ParticipantNote.choices')}
            </Label.Text>
            <Label.Text> </Label.Text>
            <Label.Text>{`${fullChoicesText}`}</Label.Text>
          </Label>
        </Tooltip>
      );
    } else {
      return undefined;
    }
  }, [fullChoicesText, participantSymptomNotes?.enabled]);

  const allowMultiSelectLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.allowMultiSelect')}
          variant="tertiary"
          testID="allow-multi-select"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.allowMultiSelect')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {i18n.t(
              `Common.${participantSymptomNotes?.allowMultiSelect ? 'yes' : 'no'}`,
            )}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.allowMultiSelect,
    participantSymptomNotes?.enabled,
  ]);

  const showOtherLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.showOther')}
          variant="tertiary"
          testID="show-other"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.showOther')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {i18n.t(
              `Common.${participantSymptomNotes?.showOther ? 'yes' : 'no'}`,
            )}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [participantSymptomNotes?.enabled, participantSymptomNotes?.showOther]);

  return (
    <Card
      title={i18n.t('Page_Study.table_config.title')}
      titleAriaLevel={1}
      titleTypeToken="$h4.strong"
    >
      <Table
        aria-label={i18n.t('Page_Study.table_config.description')}
        minHeight={0}
        height="auto"
        maxHeight="100%"
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {i18n.t('Page_Study.table_config.header.name')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {i18n.t('Page_Study.table_config.header.description')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {i18n.t('Page_Study.table_config.header.configuration')}
            </Table.HeaderCell>
            {canEdit ? <Table.HeaderCell> </Table.HeaderCell> : null}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.TextCell>
              {i18n.t('Page_Study.table_config.participant_notes.title')}
            </Table.TextCell>
            <Table.TextCell>
              <Text type="$bodySmall.default">
                {i18n.t(
                  'Page_Study.table_config.participant_notes.description',
                )}
              </Text>
            </Table.TextCell>
            <Table.TextCell>
              <HStack gap="$1" className={'participant-note-configurations'}>
                {enabledLabel}
                {intervalPromptsLabel}
                {intervalFrequencyLabel}
                {reminderFrequencyLabel}
                {firstPromptTimeLabel}
                {promptWindowLabel}
                {choicesLabel}
                {allowMultiSelectLabel}
                {showOtherLabel}
              </HStack>
            </Table.TextCell>
            {canEdit ? (
              <Table.TextCell>
                <EditParticipantNoteModal
                  initialValues={participantSymptomNotes}
                  trigger={
                    <Button
                      variant="tertiary"
                      size="small"
                      label={i18n.t('Common.edit')}
                      onPress={() => {}}
                      testID="edit-participant-note-button"
                    />
                  }
                  onSuccess={onSave}
                />
              </Table.TextCell>
            ) : (
              <Table.TextCell> </Table.TextCell>
            )}
          </Table.Row>
        </Table.Body>
      </Table>
    </Card>
  );
};
